import { Injectable, signal } from '@angular/core';
import type {
  AskRynGridAdapter,
  AskRynGridChange,
  GridView,
} from './grid-commands.models';
import {
  applyGridCommands,
  describeGridCommand,
  gridCatalogText,
  parseGridCommands,
} from './grid-commands.util';

/** A reply older than this after the question is not applied. */
const ARM_MS = 15 * 60 * 1000;

/**
 * US 35406: the bridge between Ask Ryn and the grids on the current page.
 *
 * A grid page registers an adapter while mounted. When the Ask Ryn panel sends a
 * question it calls `withGridContext`, which appends each registered grid's catalog
 * (columns and current view, never rows) to the page help and arms the registry.
 * The panel hands every `gridCommands` reply to `handle`, which validates it against
 * the catalog and applies it all or none. Only a reply NEWER than the send is
 * applied (by the server's message timestamps), because the panel re-surfaces the
 * newest action whenever it reopens a conversation, and that may be an older reply
 * the user already undid. Each handled reply moves that mark forward, so a second
 * question sent before the first reply landed still gets its reply applied. One
 * step of undo is kept per change.
 */
@Injectable({ providedIn: 'root' })
export class AskRynGridRegistry {
  private readonly adapters = new Map<string, AskRynGridAdapter>();
  /**
   * Set by a send from a page with a grid: which conversation, when, and the server
   * time of the newest reply the user had already seen (null when there was none).
   */
  private armed: {
    conversationId: string | null;
    at: number;
    after: number | null;
  } | null = null;
  private undoView: { gridKey: string; view: GridView } | null = null;
  private readonly change = signal<AskRynGridChange | null>(null);
  private readonly note = signal<string | null>(null);

  /** The last applied change, for the page's Undo chip. */
  readonly lastChange = this.change.asReadonly();

  /** Why the last reply changed nothing, for the Ask Ryn panel. Cleared on the next send. */
  readonly notice = this.note.asReadonly();

  /** Register a mounted grid. Call the returned function when it is destroyed. */
  register(adapter: AskRynGridAdapter): () => void {
    this.adapters.set(adapter.gridKey, adapter);
    return () => {
      if (this.adapters.get(adapter.gridKey) !== adapter) return;
      this.adapters.delete(adapter.gridKey);
      if (this.undoView?.gridKey === adapter.gridKey) this.dismiss();
    };
  }

  /**
   * The page help to send: unchanged when no grid is registered. `newestReplyAt` is
   * the server timestamp of the newest Ryn reply in the conversation at send time;
   * only a reply after it may change the grid.
   */
  withGridContext(
    pageHelp: string,
    conversationId: string | null = null,
    newestReplyAt: string | Date | null = null
  ): string {
    this.note.set(null);
    if (this.adapters.size === 0) return pageHelp;
    this.armed = {
      conversationId,
      at: Date.now(),
      after: toMs(newestReplyAt),
    };
    const grids = [...this.adapters.values()].map((a) =>
      gridCatalogText(a.catalog(), a.view())
    );
    return [pageHelp, ...grids].filter((t) => !!t).join('\n\n');
  }

  /**
   * Apply a `gridCommands` reply. Returns null when it was applied or ignored, or a
   * sentence for the Ask Ryn panel when nothing changed.
   */
  handle(
    action: { kind: string; document?: unknown },
    conversationId: string | null = null,
    replyAt: string | Date | null = null
  ): string | null {
    if (
      action.kind !== 'gridCommands' ||
      !this.isArmedFor(conversationId, replyAt)
    ) {
      return null;
    }
    // Stay armed for the reply to a question sent before this one landed; only a
    // reply newer than this one may apply next. Without a timestamp, disarm.
    const at = toMs(replyAt);
    const armed = this.armed;
    this.armed = at === null || !armed ? null : { ...armed, after: at };
    const gridKey = (action.document as { gridKey?: unknown } | undefined)
      ?.gridKey;
    const adapter =
      typeof gridKey === 'string' ? this.adapters.get(gridKey) : undefined;
    if (!adapter) {
      return this.refuse(
        'That grid is not on this page any more, so Ask Ryn changed nothing.'
      );
    }
    const parsed = parseGridCommands(action.document, adapter.catalog());
    if (parsed.ok === false) {
      return this.refuse(
        `Ask Ryn could not change the grid: ${parsed.error} Nothing changed.`
      );
    }
    const before = adapter.view();
    adapter.apply(applyGridCommands(before, parsed.document));
    this.undoView = { gridKey: adapter.gridKey, view: before };
    this.change.set({
      gridKey: adapter.gridKey,
      summary: parsed.document.commands
        .map((c) => describeGridCommand(c, adapter.catalog()))
        .join('; '),
    });
    return null;
  }

  /**
   * Armed by a send in the same conversation (when both sides know it), within
   * ARM_MS, and the reply is newer than the newest reply the user had seen.
   */
  private isArmedFor(
    conversationId: string | null,
    replyAt: string | Date | null
  ): boolean {
    const a = this.armed;
    if (!a || Date.now() - a.at > ARM_MS) return false;
    if (a.after !== null) {
      const at = toMs(replyAt);
      if (at === null || at <= a.after) return false;
    }
    return (
      !a.conversationId ||
      !conversationId ||
      a.conversationId === conversationId
    );
  }

  private refuse(why: string): string {
    this.note.set(why);
    return why;
  }

  /** Put back the view Ask Ryn replaced. */
  undo(gridKey: string): void {
    const adapter = this.adapters.get(gridKey);
    if (adapter && this.undoView?.gridKey === gridKey) {
      adapter.apply(this.undoView.view);
    }
    this.dismiss();
  }

  /** Forget the last change (the chip was closed, or the grid went away). */
  dismiss(): void {
    this.undoView = null;
    this.change.set(null);
  }
}

function toMs(t: string | Date | null): number | null {
  if (t === null) return null;
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
}
